import { Routes } from '@datadog/browser-rum-react/react-router-v7';
import { EvervaultProvider } from '@evervault/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TextSearch } from 'lucide-react';
import { Outlet, Route, useParams } from 'react-router';

import { Command } from '@components/features/CommandMenu';
import { useCurrentApp } from '@queries/user';
import NotFound from '@routes/NotFound';
import { ResourceCommands } from '@routes/TeamApp/ResourceCommands';
import { ENV } from '@utils/env';
import { lazyRoute } from '@utils/lazyRoute';

import { AppPending } from './AppPending';
import { BillingBounce } from './BillingBounce';

const Relays = lazyRoute(() => import('@routes/Relays'));
const Functions = lazyRoute(() => import('@routes/Functions'));

const Overview = lazyRoute(() => import('@routes/Overview'));
const Settings = lazyRoute(() => import('@routes/TeamAppSettings'));
const DataRoles = lazyRoute(() => import('@routes/DataRoles'));
const Enclaves = lazyRoute(() => import('@routes/Enclaves'));
const Logs = lazyRoute(() => import('@routes/Logs'));
const Compliance = lazyRoute(() => import('@routes/Compliance'));
const Payments = lazyRoute(() => import('@routes/Payments'));
const AsvScans = lazyRoute(() => import('@routes/AsvScans'));
const BrowserTokens = lazyRoute(() => import('@routes/BrowserTokens'));

const customEvervaultConfig = {
  urls: {
    keysUrl: ENV.apiUrls.keys,
    // The Card iframe ignores keysUrl and encrypts with the keys its host was
    // built with, so each environment points at its own host
    componentsUrl: ENV.apiUrls.components,
  },
};

const AppLayout = () => {
  const { app } = useCurrentApp();
  const { teamUuid, appUuid } = useParams();

  if (!app) {
    return <NotFound />;
  }

  if (app.status === 'pending') {
    return <AppPending />;
  }

  return (
    <BillingBounce>
      <EvervaultProvider
        teamId={teamUuid}
        appId={appUuid}
        customConfig={customEvervaultConfig}
      >
        <Outlet />

        <ResourceCommands />

        <Command
          id="logs-search"
          searchable="only"
          filterable={false}
          type="action"
          icon={<TextSearch />}
          context="app"
          group={app.name}
          priority={100000}
          label={(search) => `Search logs for "${search.trim()}"`}
          onSelect={(event, context) => {
            const params = new URLSearchParams({ query: event.search.trim() });
            context.navigate(`/${teamUuid}/${appUuid}/logs?${params}`);
          }}
        />
      </EvervaultProvider>
    </BillingBounce>
  );
};

export default function App() {
  const flags = useFlags();

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Overview />} />
        <Route path="relays/*" element={<Relays />} />
        <Route path="functions/*" element={<Functions />} />
        <Route path="enclaves/*" element={<Enclaves />} />
        <Route path="logs/*" element={<Logs />} />
        <Route path="policies" element={<DataRoles />} />
        <Route path="settings/*" element={<Settings />} />

        <Route path="payments/*" element={<Payments />} />

        {flags.browserTokens && (
          <Route path="browser-tokens/*" element={<BrowserTokens />} />
        )}

        {flags.complianceTab && (
          <Route path="compliance/*" element={<Compliance />} />
        )}

        <Route path="asv/*" element={<AsvScans />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
